import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6rsaasddmmni7ck92ag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', company: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="py-16 md:py-24 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10 md:mb-16">
          <h2
            className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-6"
            style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
          >
            {t('contact_title')}
          </h2>
          <p
            className="text-base md:text-xl text-gray-600 mb-6 md:mb-8 whitespace-pre-line"
            style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
          >
            {t('contact_sub')}
          </p>
          <a
            href="mailto:info@kgrill183.com"
            className="inline-flex items-center gap-2 md:gap-3 text-base md:text-lg text-teal-500 hover:text-teal-600 transition-colors duration-300 cursor-pointer"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            <i className="ri-mail-line text-xl md:text-2xl"></i>
            info@kgrill183.com
          </a>
        </div>

        <div className="bg-white rounded-lg p-5 md:p-8 lg:p-12">
          <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                  style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
                >
                  {t('contact_name')} *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all duration-300"
                  style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
                />
              </div>

              <div>
                <label
                  htmlFor="company"
                  className="block text-sm font-medium text-gray-700 mb-2"
                  style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
                >
                  {t('contact_company')}
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all duration-300"
                  style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                  style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
                >
                  {t('contact_email')} *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all duration-300"
                  style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
                />
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700 mb-2"
                  style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
                >
                  {t('contact_phone')}
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all duration-300"
                  style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
                />
              </div>
            </div>

            <div className="mb-6">
              <label
                htmlFor="message"
                className="block text-sm font-medium text-gray-700 mb-2"
                style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
              >
                {t('contact_message')} *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={6}
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none transition-all duration-300"
                style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
              ></textarea>
              <p className="text-xs text-gray-500 mt-2" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
                {formData.message.length}/500
              </p>
            </div>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-700" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
                  {t('contact_success')}
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
                  {t('contact_error')}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-teal-500 text-white py-4 rounded-md font-medium hover:bg-teal-600 transition-colors duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
            >
              {isSubmitting ? t('contact_submitting') : t('contact_submit')}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
